import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useT } from '../i18n/i18n';
import Markdown from './Markdown';
import { STATUS_LABEL, fetchGuide, formatDate, reviewGuide, type GuideView } from './guides';

export default function GuidePage() {
  const t = useT();
  const { slug = '' } = useParams();
  const [view, setView] = useState<GuideView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  useEffect(() => {
    setView(null);
    setError(null);
    fetchGuide(slug).then(setView).catch(() => setError(t('This guide is not available.')));
  }, [slug]);

  async function review(decision: 'APPROVED' | 'CHANGES') {
    setBusy(true);
    setReviewError(null);
    try {
      setView(await reviewGuide(slug, decision, note));
      setNote('');
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div className="card">
        <p className="error" role="alert">{error}</p>
        <Link to="/assist/learn">{t('Back to the guides')}</Link>
      </div>
    );
  }
  if (!view) return <p className="muted" role="status">{t('Loading…')}</p>;

  const g = view.guide;
  const showReview = view.reviewerNotes !== null;

  return (
    <div className="guide">
      <article className="card">
        <p className="field-hint" style={{ marginTop: 0 }}><Link to="/assist/learn">{t('Learn about autism')}</Link></p>
        {g.status !== 'PUBLISHED' && (
          <div className="banner guide-draft" role="note">
            <strong>{t(STATUS_LABEL[g.status])}.</strong> {t('Parents cannot see this guide until a reviewer approves it.')}
          </div>
        )}
        <h1>{g.title}</h1>
        <p className="guide-summary">{g.summary}</p>
        <Markdown text={view.body} />
        <div className="guide-footer">
          {g.status === 'PUBLISHED' && g.reviewedBy && g.reviewedAt && (
            <p>{t('Checked by {name}, {role}, on {date}.', { name: g.reviewedBy, role: g.reviewedRole ?? '', date: formatDate(g.reviewedAt) })}</p>
          )}
          <p>{t('This guide is general information, not medical advice. Speak to a GP or another professional about your own child.')}</p>
          <p>{t('Have a question about this?')} <Link to="/assist/ai">{t('Ask EasyLearning AI')}</Link> {t('or')} <Link to="/assist/experts">{t('ask a consultant')}</Link>.</p>
        </div>
      </article>

      {showReview && (
        <section className="card guide-review" aria-labelledby="review-h">
          <h2 id="review-h">{t('For the reviewer')}</h2>
          {view.reviewerNotes ? <Markdown text={view.reviewerNotes} /> : <p className="muted">{t('No notes from the author.')}</p>}

          {view.canReview ? (
            <div className="guide-review-form">
              <div className="field">
                <label htmlFor="review-note">{t('Your notes')}</label>
                <textarea
                  id="review-note"
                  rows={5}
                  maxLength={4000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('What needs to change, or anything you checked')}
                />
              </div>
              {reviewError && <div className="error" role="alert">{reviewError}</div>}
              <div className="toolbar">
                <button type="button" className="btn" disabled={busy} onClick={() => void review('APPROVED')}>
                  {t('Approve and publish')}
                </button>
                <button type="button" className="btn secondary" disabled={busy || !note.trim()} onClick={() => void review('CHANGES')}>
                  {t('Ask for changes')}
                </button>
              </div>
              <p className="field-hint">{t('Approving publishes this exact text with your name and role. If the guide is edited afterwards, it goes back to waiting for review.')}</p>
            </div>
          ) : (
            <p className="field-hint">{t('Only an approved consultant can approve or ask for changes.')}</p>
          )}

          {view.history.length > 0 && (
            <>
              <h3>{t('Review history')}</h3>
              <ul className="guide-history">
                {view.history.map((h, i) => (
                  <li key={i}>
                    <strong>{h.decision === 'APPROVED' ? t('Approved') : t('Changes asked for')}</strong> {t('by {name}, {date}', { name: h.reviewerName, date: formatDate(h.createdAt) })}
                    {!h.currentVersion && ` ${t('(an earlier version)')}`}
                    {h.note && <div className="guide-history-note">{h.note}</div>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </div>
  );
}
